/**
 * Exponential Backoff Utility
 * Provides retry logic with exponential backoff for handling transient failures
 */

export interface BackoffOptions {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  factor?: number;
  jitter?: boolean;
}

export class ExponentialBackoff {
  private readonly maxRetries: number;
  private readonly initialDelay: number;
  private readonly maxDelay: number;
  private readonly factor: number;
  private readonly jitter: boolean;

  constructor(options: BackoffOptions = {}) {
    this.maxRetries = options.maxRetries ?? 3;
    this.initialDelay = options.initialDelay ?? 1000; // 1 second
    this.maxDelay = options.maxDelay ?? 30000; // 30 seconds
    this.factor = options.factor ?? 2;
    this.jitter = options.jitter ?? true;
  }

  async execute<T>(
    operation: () => Promise<T>,
    shouldRetry?: (error: any) => boolean
  ): Promise<T> {
    let lastError: any;
    
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        
        // Check if we should retry
        if (attempt === this.maxRetries) {
          throw error;
        }
        
        if (shouldRetry && !shouldRetry(error)) {
          throw error;
        }
        
        // Calculate delay
        const delay = this.calculateDelay(attempt);
        await this.sleep(delay);
      }
    }
    
    throw lastError;
  }

  private calculateDelay(attempt: number): number {
    const exponentialDelay = this.initialDelay * Math.pow(this.factor, attempt);
    const delay = Math.min(exponentialDelay, this.maxDelay);
    
    if (this.jitter) {
      // Add random jitter (±25% of the delay)
      const jitterAmount = delay * 0.25;
      return delay + (Math.random() * 2 - 1) * jitterAmount;
    }
    
    return delay;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Pre-configured instances for common use cases
export const authBackoff = new ExponentialBackoff({
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 10000,
  factor: 2,
  jitter: true
});

export const apiBackoff = new ExponentialBackoff({
  maxRetries: 5,
  initialDelay: 500,
  maxDelay: 30000,
  factor: 2,
  jitter: true
});